import {offcanvas} from 'bootstrap';

const SCRIPT_NAME = 'init_navigationWithOffcanvas.js';

/**
 * Initialisiert die Offcanvas-Logik für die Navbar
 */
export function initNavigationWithOffcanvas() {

    const PIN_KEY = 'submenuPinned';
    const PINNED_OPTIONS = Object.freeze({backdrop: false, scroll: true});

    const offcanvasElements = document.querySelectorAll('.offcanvas');
    const pinButtons = document.querySelectorAll('.pin-toggle');
    const iconLinks = document.querySelectorAll('.icon-sidebar a[data-bs-target]');

    //TODO: Globale Cookie Functions nutzen?

    // --- Cookie Helpers ---
    const setCookie = (name, value, days = 30) => {
        const d = new Date();
        d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};path=/;expires=${d.toUTCString()};SameSite=Lax`;
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const deleteCookie = (name) => {
        document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    };

    /**
     * Prüft, ob ein Menü angeheftet ist
     */
    const isPinnedActive = () => !!getCookie(PIN_KEY);

    /**
     * Aktualisiert die Pin-Icons in allen Submenüs
     */
    const syncPinIcons = () => {
        const pinnedId = getCookie(PIN_KEY);

        pinButtons.forEach(btn => {
            const offcanvas = btn.closest('.offcanvas');
            const icon = btn.querySelector('span');
            if (!icon || !offcanvas?.id) return;

            const isPinned = pinnedId === offcanvas.id;
            icon.classList.toggle('fas', isPinned);
            icon.classList.toggle('fal', !isPinned);
        });

        // Toggle-Verhalten der Sidebar-Links steuern
        iconLinks.forEach(link => {
            const targetSelector = link.getAttribute('data-bs-target');
            const targetId = targetSelector?.slice(1);

            if (pinnedId && pinnedId === targetId) {
                // Wenn dieses Menü gepinnt ist: Bootstrap-Automatismus deaktivieren
                link.removeAttribute('data-bs-toggle');
            } else {
                // Ansonsten: Bootstrap darf togglen/öffnen
                link.setAttribute('data-bs-toggle', 'offcanvas');
            }
        });
    };

    /**
     * Schließt alle Offcanvas-Elemente außer dem angegebenen
     */
    const hideAllExcept = (targetId) => {
        offcanvasElements.forEach(el => {
            if (el.id === targetId) return;
            bootstrap.Offcanvas.getInstance(el)?.hide();
        });
    };

    /**
     * Initialisiert das angeheftete Menü (Rehydrierung für Bootstrap)
     */
    const openPinnedMenu = () => {
        const pinnedId = getCookie(PIN_KEY);
        if (!pinnedId) return;

        const el = document.getElementById(pinnedId);
        if (el) {
            // Da PHP die 'show' Klasse bereits gesetzt hat,
            // erstellen wir hier nur die Instanz für die Interaktion.
            bootstrap.Offcanvas.getOrCreateInstance(el, PINNED_OPTIONS);
        }
    };

    /**
     * Registriert Click-Handler für Icon-Links (Sidebar)
     */
    const initIconLinks = () => {
        iconLinks.forEach(link => {
            link.addEventListener('click', (e) => {

                const targetSelector = link.getAttribute('data-bs-target');
                if (!targetSelector?.startsWith('#')) return;

                const targetId = targetSelector.slice(1);
                const targetEl = document.getElementById(targetId);
                if (!targetEl) return;

                const currentlyPinnedId = getCookie(PIN_KEY);

                // Wenn ein Pin aktiv ist, managen wir den Wechsel manuell (Tab-Verhalten)
                if (currentlyPinnedId) {
                    // Klick auf bereits gepinntes Menü -> Nichts tun (da data-bs-toggle weg ist, passiert eh nichts)
                    if (currentlyPinnedId === targetId) {
                        e.preventDefault();

                        // Subtile Animation triggern, um Aktivität zu zeigen
                        targetEl.classList.add('is-nudged');
                        setTimeout(() => {
                            targetEl.classList.remove('is-nudged');
                        }, 300); // Entspricht der Animationsdauer im SCSS

                        return;
                    }

                    // Klick auf ein ANDERES Menü -> Wechseln
                    e.preventDefault();
                    hideAllExcept(targetId);
                    setCookie(PIN_KEY, targetId);
                    document.body.classList.add('menu-pinned', 'submenu-open');
                    bootstrap.Offcanvas.getOrCreateInstance(targetEl, PINNED_OPTIONS).show();
                    syncPinIcons();
                }
            });
        });
    };

    /**
     * Registriert Click-Handler für Pin-Buttons
     */
    const initPinButtons = () => {
        pinButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();

                const offcanvas = btn.closest('.offcanvas');
                if (!offcanvas?.id) return;

                const {id} = offcanvas;
                const isPinned = getCookie(PIN_KEY) === id;

                if (isPinned) {
                    deleteCookie(PIN_KEY);
                    document.body.classList.remove('menu-pinned', 'submenu-open');
                    bootstrap.Offcanvas.getInstance(offcanvas)?.hide();
                } else {
                    setCookie(PIN_KEY, id);
                    document.body.classList.add('menu-pinned', 'submenu-open');
                    document.body.style.overflow = '';
                    document.body.style.padding = '';
                    bootstrap.Offcanvas.getOrCreateInstance(offcanvas, PINNED_OPTIONS).show();
                }
                syncPinIcons();
            });
        });
    };

    const initOffcanvasCloseHandlers = () => {
        offcanvasElements.forEach(el => {
            el.addEventListener('hidden.bs.offcanvas', () => {
                if (!getCookie(PIN_KEY)) {
                    document.body.classList.remove('submenu-open');
                }
                syncPinIcons();
            });
        });
    };

    const initResponsiveHandler = () => {
        const mediaQuery = window.matchMedia('(min-width: 992px)');
        const handleBreakpointChange = (e) => {
            if (!e.matches && getCookie(PIN_KEY)) {
                deleteCookie(PIN_KEY);
                document.body.classList.remove('menu-pinned', 'submenu-open');
                offcanvasElements.forEach(el => bootstrap.Offcanvas.getInstance(el)?.hide());
                syncPinIcons();
            }
        };
        mediaQuery.addEventListener('change', handleBreakpointChange);
    };

    const initMobileMenuToggle = () => {
        const toggleBtn = document.querySelector('.mobile-menu-toggle');
        if (!toggleBtn) return;
        toggleBtn.addEventListener('click', () => {
            const isOpen = document.body.classList.contains('mobile-menu-open');
            document.body.classList.toggle('mobile-menu-open', !isOpen);
            if (isOpen) {
                offcanvasElements.forEach(el => bootstrap.Offcanvas.getInstance(el)?.hide());
            }
        });
    };

    /**
     * Registriert Handler für Links, die das Offcanvas zwingend unpinnen sollen
     */
    const initUnpinLinks = () => {
        document.addEventListener('click', (e) => {
            const link = e.target.closest('[data-unpin-offcanvas="true"]');
            if (!link) return;

            const pinnedId = getCookie(PIN_KEY);
            if (pinnedId) {
                // Cookie löschen
                deleteCookie(PIN_KEY);
                document.body.classList.remove('menu-pinned', 'submenu-open');

                // Offcanvas schließen
                const pinnedEl = document.getElementById(pinnedId);
                if (pinnedEl) {
                    bootstrap.Offcanvas.getInstance(pinnedEl)?.hide();
                }

                syncPinIcons();
            }
            // Link-Navigation wird normal fortgesetzt (kein preventDefault)
        });
    };

    const init = () => {
        initIconLinks();
        initPinButtons();
        initOffcanvasCloseHandlers();
        initResponsiveHandler();
        initMobileMenuToggle();
        initUnpinLinks(); // NEU
        openPinnedMenu();
        syncPinIcons();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}